import { useMemo, useState } from "react";
import type { VatDeclarationDrillDownBill, VatDeclarationFormData, VatDeclarationTransactionContribution } from "../types/index.ts";
import { mapVatDocsToDrillDownRows, mapVatTransactionsToDrillDownRows } from "../utils/mapVatDocsToDrillDownRows.ts";
import {
    Money,
    toCentsFromMoney,
    moneyToLocalizedString,
    DisplayLocale,
} from "../../../../lib/Money.ts";
import { DrillDownModal } from "../../DrillDown/index.ts";
import { VatDeclarationRowLabel } from "./VatDeclarationRowLabel.tsx";

const INITIAL_FORM_DATA = {
    'subsection_1a': { value: '0', vat: '0' },
    'subsection_1b': { value: '0', vat: '0' },
    'subsection_1c': { value: '0', vat: '0' },
    'subsection_1d': { value: '0', vat: '0' },
    'subsection_1e': { value: '0', vat: '0' },
    'subsection_2a': { value: '0', vat: '0' },
    'subsection_3a': { value: '0' },
    'subsection_3b': { value: '0' },
    'subsection_3c': { value: '0' },
    'subsection_4a': { value: '0', vat: '0' },
    'subsection_4b': { value: '0', vat: '0' },
    'subsection_5a': { vat: '0' },
    'subsection_5b': { vat: '0' },
    'subsection_5c': { vat: '0' },
    'subsection_5d': { vat: '0' },
    'subsection_5e': { vat: '0' },
    'subsection_5f': { vat: '0' },
    'subsection_5g': { vat: '0' },
};

function SectionTitle({ 
    number, 
    title
}: { 
    number: string, 
    title: string
}) {
    return (
        <div className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-1 flex items-center justify-center">
                <div className="bg-[#FFDED8] rounded-full w-10 h-10 flex items-center justify-center">
                    <span className="text-gray-900 font-semibold text-xl">{number}</span>
                </div>
            </div>
            <div className="col-span-11 flex items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900 leading-tight ">
                    {title}
                </h2>
                <div className="h-px bg-[#FFCDC580] flex-1"></div>
            </div>
        </div>
    );
}

function MoneyInput({ value, ariaLabel }: { value: string, ariaLabel: string }) {
    return (
        <div className="flex items-center bg-gray-200 rounded-[8px] overflow-hidden h-[42px] p-[1.5px]">
            <span className="flex items-center justify-center px-2 h-full bg-gray-200 text-gray-900 text-sm">€</span>
            <input
                type="text"
                value={value}
                readOnly
                aria-label={ariaLabel}
                className="px-4 h-full bg-white border-0 text-gray-900 text-sm focus:outline-none flex-1 min-w-0 rounded-[8px]"
            />
        </div>
    );
}

function InputRow({
    rowNumber,
    label,
    subLabel,
    value,
    vatValue,
    isTotal = false,
    iconType = 'none',
    showNet = false,
    showVat = false,
    onViewContributions,
    contributionCount,
}: {
    rowNumber: string,
    label: string,
    subLabel?: string,
    value?: string,
    vatValue?: string,
    isTotal?: boolean,
    iconType?: 'none' | 'plus' | 'minus' | 'plus-minus',
    showNet?: boolean,
    showVat?: boolean,
    onViewContributions?: () => void,
    contributionCount?: number,
}) {

    function renderIcon() {
        if (iconType === 'none') return null;

        if (iconType === 'plus-minus') {
            return (
                <div className="text-gray-400 hover:text-gray-600 text-xl font-light flex items-center justify-center w-6 h-6">+/-</div>
            );
        }
        const iconSymbol = iconType === 'plus' ? '+' : '−';
        return (
            <div className="text-gray-400 hover:text-gray-600 text-xl font-light flex items-center justify-center w-6 h-6">
                {iconSymbol}
            </div>
        );
    }

    return (
        <div className={`grid grid-cols-12 gap-2 items-center ${isTotal ? 'border-t border-gray-300 pt-3 mt-3' : ''}`}>
            <div className="col-span-1 flex items-center justify-center text-sm text-gray-600 font-medium">
                {rowNumber}
            </div>
            <VatDeclarationRowLabel
                label={label}
                subLabel={subLabel}
                isTotal={isTotal}
                onViewContributions={onViewContributions}
                contributionCount={contributionCount}
            />

            <div className="col-span-2 flex items-center gap-2">
                {showNet && <MoneyInput value={value || ''} ariaLabel={`${rowNumber} ${label} bedrag`} />}
            </div>

            <div className="col-span-2 flex items-center gap-2">
                {showVat && <MoneyInput value={vatValue || ''} ariaLabel={`${rowNumber} ${label} omzetbelasting`} />}
            </div>
            <div className="col-span-1 flex items-center gap-1">
                {renderIcon()}
            </div>

        </div>
    );
}

function Line({
    colStart,
    colEnd,
    height = '1px',
    color = '#D1D5DB',
    rowHeight = '20px'
}: {
    colStart: number,
    colEnd: number,
    height?: string,
    color?: string,
    rowHeight?: string
}) {
    return (
        <div className="grid grid-cols-12 gap-2 items-center" style={{ minHeight: rowHeight }}>
            <div
                style={{
                    gridColumn: `${colStart} / ${colEnd + 1}`,
                    height: height,
                    backgroundColor: color
                }}
            ></div>
        </div>
    );
}

type VatDeclarationBillKey = {
    [K in keyof VatDeclarationFormData]: VatDeclarationFormData[K] extends VatDeclarationDrillDownBill[] ? K : never
}[keyof VatDeclarationFormData];

type VatDeclarationTransactionKey = {
    [K in keyof VatDeclarationFormData]: VatDeclarationFormData[K] extends VatDeclarationTransactionContribution[] ? K : never
}[keyof VatDeclarationFormData];

type SelectedContributions = {
    billKey: VatDeclarationBillKey;
    transactionsKey?: VatDeclarationTransactionKey;
    title: string;
};

export interface VatDeclarationFormProps {
    businessId: string;
    vatDeclaration: VatDeclarationFormData | null;
    onDocumentClick?: (documentId: string, fileName: string | null) => void;
}

function formatMoney(money: Money | undefined, absoluteValue: boolean = false): string {
    return moneyToLocalizedString(money, DisplayLocale.NL_NL, 0, absoluteValue);
}

function toFormData(vatDeclaration: VatDeclarationFormData | null): typeof INITIAL_FORM_DATA {
    if (!vatDeclaration) {
        return INITIAL_FORM_DATA;
    }

    return {
        'subsection_1a': { value: formatMoney(vatDeclaration.subsection_1a_amount), vat: formatMoney(vatDeclaration.subsection_1a_vat) },
        'subsection_1b': { value: formatMoney(vatDeclaration.subsection_1b_amount), vat: formatMoney(vatDeclaration.subsection_1b_vat) },
        'subsection_1c': { value: formatMoney(vatDeclaration.subsection_1c_amount), vat: formatMoney(vatDeclaration.subsection_1c_vat) },
        'subsection_1d': { value: formatMoney(vatDeclaration.subsection_1d_amount), vat: formatMoney(vatDeclaration.subsection_1d_vat) },
        'subsection_1e': { value: formatMoney(vatDeclaration.subsection_1e_amount), vat: formatMoney(vatDeclaration.subsection_1e_vat) },
        'subsection_2a': { value: formatMoney(vatDeclaration.subsection_2a_amount), vat: formatMoney(vatDeclaration.subsection_2a_vat) },
        'subsection_3a': { value: formatMoney(vatDeclaration.subsection_3a) },
        'subsection_3b': { value: formatMoney(vatDeclaration.subsection_3b) },
        'subsection_3c': { value: formatMoney(vatDeclaration.subsection_3c) },
        'subsection_4a': { value: formatMoney(vatDeclaration.subsection_4a_amount), vat: formatMoney(vatDeclaration.subsection_4a_vat) },
        'subsection_4b': { value: formatMoney(vatDeclaration.subsection_4b_amount), vat: formatMoney(vatDeclaration.subsection_4b_vat) },
        'subsection_5a': { vat: formatMoney(vatDeclaration.subsection_5a) },
        'subsection_5b': { vat: formatMoney(vatDeclaration.subsection_5b) },
        'subsection_5c': { vat: formatMoney(vatDeclaration.subsection_5c) },
        'subsection_5d': { vat: formatMoney(vatDeclaration.subsection_5d) },
        'subsection_5e': { vat: formatMoney(vatDeclaration.subsection_5e) },
        'subsection_5f': { vat: formatMoney(vatDeclaration.subsection_5f) },
        'subsection_5g': { vat: formatMoney(vatDeclaration.subsection_5g) },
    };
}

export function VatDeclarationForm({ businessId, vatDeclaration, onDocumentClick }: VatDeclarationFormProps) {
    const formData = useMemo(() => toFormData(vatDeclaration), [vatDeclaration]);
    const [selectedContributions, setSelectedContributions] = useState<SelectedContributions | null>(null);

    const openContributionsPopup = (key: VatDeclarationBillKey, title: string, transactionsKey?: VatDeclarationTransactionKey) => {
        if (!vatDeclaration) {
            return;
        }

        setSelectedContributions({ billKey: key, transactionsKey, title });
    };

    const sectionContributionCount = (key: VatDeclarationBillKey, transactionsKey?: VatDeclarationTransactionKey): number =>
        (vatDeclaration?.[key]?.length ?? 0) + (transactionsKey ? (vatDeclaration?.[transactionsKey]?.length ?? 0) : 0);

    const selectedContributionRows = useMemo(() => {
        if (!selectedContributions || !vatDeclaration) {
            return [];
        }
        const bills = vatDeclaration[selectedContributions.billKey] ?? [];
        const transactions = selectedContributions.transactionsKey
            ? vatDeclaration[selectedContributions.transactionsKey] ?? []
            : [];
        return [
            ...mapVatDocsToDrillDownRows(bills),
            ...mapVatTransactionsToDrillDownRows(transactions),
        ];
    }, [selectedContributions, vatDeclaration]);

    const subsection5gMoney = vatDeclaration?.subsection_5g;
    const subsection5gSignedCents = subsection5gMoney ? toCentsFromMoney(subsection5gMoney) : 0;
    const subsection5gAbsRoundedDecimalString = formatMoney(subsection5gMoney, true);

    return (
        <>
            <div className="bg-[#FFEDEA] rounded-[20px] p-6 border border-pink-100">
                <div className="grid grid-cols-12 gap-3 mb-4 px-4 items-start">
                    <h1 className="col-span-7 text-lg font-bold text-gray-900 leading-tight">Gegevens omzet en omzetbelasting</h1>
                    <div className="col-span-2 text-sm text-gray-600">
                        <span className="whitespace-nowrap">Grondslag</span>
                    </div>
                    <div className="col-span-2 text-sm text-gray-600">
                        <span className="whitespace-nowrap">Omzetbelasting</span>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Section 1 */}
                    <section>
                        <SectionTitle number="1" title="Prestaties binnenland" />
                        <div className="space-y-1">
                            <InputRow
                                rowNumber="1a"
                                label="Leveringen/diensten belast met 21%"
                                value={formData['subsection_1a'].value}
                                vatValue={formData['subsection_1a'].vat}
                                showNet
                                showVat
                                onViewContributions={() => openContributionsPopup('subsection_1a_bills', '1a – Leveringen/diensten belast met 21%', 'subsection_1a_transactions')}
                                contributionCount={sectionContributionCount('subsection_1a_bills', 'subsection_1a_transactions')}
                            />
                            <InputRow
                                rowNumber="1b"
                                label="Leveringen/diensten belast met 9%"
                                value={formData['subsection_1b'].value}
                                vatValue={formData['subsection_1b'].vat}
                                showNet
                                showVat
                                onViewContributions={() => openContributionsPopup('subsection_1b_bills', '1b – Leveringen/diensten belast met 9%', 'subsection_1b_transactions')}
                                contributionCount={sectionContributionCount('subsection_1b_bills', 'subsection_1b_transactions')}
                            />
                            <InputRow
                                rowNumber="1c"
                                label="Leveringen/diensten belast met overige tarieven behalve 0%"
                                value={formData['subsection_1c'].value}
                                vatValue={formData['subsection_1c'].vat}
                                showNet
                                showVat
                                onViewContributions={() => openContributionsPopup('subsection_1c_bills', '1c – Leveringen/diensten belast met overige tarieven behalve 0%')}
                                contributionCount={vatDeclaration?.subsection_1c_bills?.length}
                            />
                            <InputRow
                                rowNumber="1d"
                                label="Privé-gebruik"
                                value={formData['subsection_1d'].value}
                                vatValue={formData['subsection_1d'].vat}
                                showNet
                                showVat
                                onViewContributions={() => openContributionsPopup('subsection_1d_bills', '1d – Privé-gebruik')}
                                contributionCount={vatDeclaration?.subsection_1d_bills?.length}
                            />
                            <InputRow
                                rowNumber="1e"
                                label="Leveringen/diensten belast met 0% of niet bij u belast"
                                value={formData['subsection_1e'].value}
                                vatValue={formData['subsection_1e'].vat}
                                showNet
                                onViewContributions={() => openContributionsPopup('subsection_1e_bills', '1e – Leveringen/diensten belast met 0% of niet bij u belast', 'subsection_1e_transactions')}
                                contributionCount={sectionContributionCount('subsection_1e_bills', 'subsection_1e_transactions')}
                            />
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section>
                        <SectionTitle number="2" title="Verleggingsregelingen binnenland" />
                        <div className="space-y-1">
                            <InputRow
                                rowNumber="2a"
                                label="Leveringen/diensten waarbij de heffing van omzetbelasting naar u is verlegd."
                                value={formData['subsection_2a'].value}
                                vatValue={formData['subsection_2a'].vat}
                                showNet
                                showVat
                                onViewContributions={() => openContributionsPopup('subsection_2a_bills', '2a – Leveringen/diensten waarbij de heffing van omzetbelasting naar u is verlegd', 'subsection_2a_transactions')}
                                contributionCount={sectionContributionCount('subsection_2a_bills', 'subsection_2a_transactions')}
                            />
                        </div>
                    </section>


                    <section>
                        <SectionTitle number="3" title="Prestaties naar/in het buitenland" />
                        <div className="space-y-1">
                            <InputRow
                                rowNumber="3a"
                                label="Leveringen naar landen buiten de EU (uitvoer)"
                                value={formData['subsection_3a'].value}
                                showNet
                                onViewContributions={() => openContributionsPopup('subsection_3a_bills', '3a – Leveringen naar landen buiten de EU (uitvoer)', 'subsection_3a_transactions')}
                                contributionCount={sectionContributionCount('subsection_3a_bills', 'subsection_3a_transactions')}
                            />
                            <InputRow
                                rowNumber="3b"
                                label="Leveringen naar/diensten in landen binnen de EU"
                                value={formData['subsection_3b'].value}
                                showNet
                                onViewContributions={() => openContributionsPopup('subsection_3b_bills', '3b – Leveringen naar/diensten in landen binnen de EU', 'subsection_3b_transactions')}
                                contributionCount={sectionContributionCount('subsection_3b_bills', 'subsection_3b_transactions')}
                            />
                            <InputRow
                                rowNumber="3c"
                                label="Installatie/afstandsverkopen binnen de EU"
                                value={formData['subsection_3c'].value}
                                showNet
                            />
                        </div>
                    </section>

                    {/* Section 4 */}
                    <section>
                        <SectionTitle number="4" title="Prestaties uit het buitenland aan u verricht" />
                        <div className="space-y-1">
                            <InputRow
                                rowNumber="4a"
                                label="Leveringen/diensten uit landen buiten de EU"
                                value={formData['subsection_4a'].value}
                                vatValue={formData['subsection_4a'].vat}
                                showNet
                                showVat
                                onViewContributions={() => openContributionsPopup('subsection_4a_bills', '4a – Leveringen/diensten uit landen buiten de EU', 'subsection_4a_transactions')}
                                contributionCount={sectionContributionCount('subsection_4a_bills', 'subsection_4a_transactions')}
                            />
                            <InputRow
                                rowNumber="4b"
                                label="Leveringen/diensten uit landen binnen de EU"
                                value={formData['subsection_4b'].value}
                                vatValue={formData['subsection_4b'].vat}
                                showNet
                                showVat
                                iconType="plus"
                                onViewContributions={() => openContributionsPopup('subsection_4b_bills', '4b – Leveringen/diensten uit landen binnen de EU', 'subsection_4b_transactions')}
                                contributionCount={sectionContributionCount('subsection_4b_bills', 'subsection_4b_transactions')}
                            />
                        </div>
                        <Line colStart={8} colEnd={11} height="2px" color="#C7C7C7" />
                    </section>

                    {/* Section 5 */}
                    <section>
                        <SectionTitle number="5" title="Voorbelasting, kleineondernemersregeling, schatting en eindtotaal" />
                        <div className="space-y-1">
                            <InputRow
                                rowNumber="5a"
                                label="Verschuldigde omzetbelasting: tel de omzetbelasting van de rubrieken 1a t/m 4b op"
                                vatValue={formData['subsection_5a'].vat}
                                showVat
                            />
                            <InputRow
                                rowNumber="5b"
                                label="Voorbelasting"
                                vatValue={formData['subsection_5b'].vat}
                                showVat
                                iconType="minus"
                                onViewContributions={() => openContributionsPopup('subsection_5b_bills', '5b – Voorbelasting', 'subsection_5b_transactions')}
                                contributionCount={sectionContributionCount('subsection_5b_bills', 'subsection_5b_transactions')}
                            />
                            <Line colStart={8} colEnd={11} height="2px" color="#C7C7C7" />

                            <InputRow
                                rowNumber="5c"
                                label="Subtotaal: bereken 5a min 5b"
                                vatValue={formData['subsection_5c'].vat}
                                showVat
                            />
                            <InputRow
                                rowNumber="5d"
                                label="Vermindering volgens de kleineondernemersregeling"
                                vatValue={formData['subsection_5d'].vat}
                                showVat
                            />
                            <InputRow
                                rowNumber="5e"
                                label="Schatting vorige aangifte(n). Alleen als de Belastingdienst u toestemming heeft gegeven"
                                vatValue={formData['subsection_5e'].vat}
                                showVat
                            />
                            <InputRow
                                rowNumber="5f"
                                label="Schatting deze aangifte. Alleen als de Belastingdienst u toestemming heeft gegeven"
                                vatValue={formData['subsection_5f'].vat}
                                showVat
                                iconType="plus-minus"
                            />
                            <Line colStart={8} colEnd={11} height="2px" color="#C7C7C7" />

                        </div>
                    </section>

                    <section>
                        <div className="space-y-1">
                            <div className="grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-1 flex items-center justify-center text-sm text-gray-600 font-medium ">5g</div>
                                <div className="col-span-1">
                                    <label className="block text-sm font-semibold text-gray-900 whitespace-nowrap">Totaal</label>
                                </div>
                                <div className="col-span-2 text-sm text-gray-500 pl-2 whitespace-nowrap">
                                    {subsection5gSignedCents >= 0 ? 'te betalen' : 'terug te vragen'}
                                </div>
                                <div className="col-start-10 col-span-2 flex justify-end">

                                        <MoneyInput value={subsection5gAbsRoundedDecimalString} ariaLabel="5g Totaal" />

                                </div>

                            </div>

                        </div>
                    </section>
                </div>
            </div>
            <DrillDownModal
                open={selectedContributions !== null}
                onClose={() => setSelectedContributions(null)}
                title={selectedContributions?.title ?? ''}
                businessId={businessId}
                rows={selectedContributionRows}
                onDocumentClick={onDocumentClick}
            />
        </>
    );
}
